import React from 'react';
import { Alert, Card, Col, Row, Space, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { epcEstimationService } from '@/services/epcEstimation.service';

const { Text } = Typography;

const SECTION_LABELS: Record<string, string> = {
  foundation: 'Foundation',
  direct_cost: 'Direct cost',
  resources: 'Resources',
  subcontract: 'Subcontract',
  project_cost: 'Project cost',
  commercial_pricing: 'Commercial pricing',
  governance: 'Governance',
  submission_basis: 'Submission basis',
};

function statusColor(st: string): string {
  if (st === 'READY' || st === 'CERTIFIABLE' || st === 'CERTIFIED') return 'green';
  if (st === 'BLOCKED' || st === 'STALE') return 'red';
  if (st === 'IN_PROGRESS') return 'orange';
  return 'default';
}

export interface EpcEstimateGovernanceReadinessPanelProps {
  estimateId: string;
}

/**
 * EST3 UX2 Wave 8 — Governance readiness (sections, profiles, blockers, checks).
 */
export const EpcEstimateGovernanceReadinessPanel: React.FC<EpcEstimateGovernanceReadinessPanelProps> = ({
  estimateId,
}) => {
  const readinessQ = useQuery({
    queryKey: ['epc-est-readiness', estimateId],
    queryFn: () => epcEstimationService.readiness(estimateId),
    enabled: !!estimateId,
  });

  const data = readinessQ.data as Record<string, any> | undefined;
  const sections = (data?.sections || {}) as Record<string, any>;
  const blockers = (data?.blockers || []) as Array<{ code: string; message: string }>;
  const warnings = (data?.warnings || []) as Array<{ code: string; message: string }>;
  const profiles = data?.profiles || {};
  const checks = (data?.checks || []) as Array<{ key: string; label: string; ok?: boolean; pending?: boolean }>;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Card size="small" title="Readiness sections">
        <Row gutter={[8, 8]}>
          {Object.entries(SECTION_LABELS).map(([key, label]) => {
            const sec = sections[key];
            const st = sec?.status || '—';
            return (
              <Col key={key} xs={12} md={6}>
                <Tag color={statusColor(String(st))}>
                  {label}: {st}
                </Tag>
              </Col>
            );
          })}
        </Row>
        <Space wrap style={{ marginTop: 12 }}>
          <Tag color={profiles.WORKING_ESTIMATE?.ready ? 'green' : 'orange'}>Working estimate</Tag>
          <Tag color={profiles.COMMERCIAL_REVIEW?.ready ? 'green' : 'red'}>
            Commercial review {profiles.COMMERCIAL_REVIEW?.ready ? 'READY' : 'BLOCKED'}
          </Tag>
          <Tag color={statusColor(String(profiles.SUBMISSION_BASIS?.status || 'NOT_CERTIFIED'))}>
            Submission basis {profiles.SUBMISSION_BASIS?.status || 'NOT_CERTIFIED'}
          </Tag>
        </Space>
        {data?.section_counts ? (
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            Sections ready {data.section_counts.ready}/{data.section_counts.total}
            {data.section_counts.blocked ? ` · blocked ${data.section_counts.blocked}` : ''}
          </Text>
        ) : null}
      </Card>

      <Card size="small" title="Readiness checks">
        <Space wrap>
          {checks.map((c) => (
            <Tag key={c.key} color={c.pending ? 'default' : c.ok ? 'green' : 'red'}>
              {c.label} {c.pending ? '(later wave)' : c.ok ? '✓' : '✕'}
            </Tag>
          ))}
        </Space>
      </Card>

      {blockers.length > 0 ? (
        <Alert
          type="error"
          showIcon
          message="Blockers"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {blockers.slice(0, 12).map((b) => (
                <li key={b.code}>
                  {b.code}: {b.message}
                </li>
              ))}
            </ul>
          }
        />
      ) : null}

      {warnings.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          message="Warnings"
          description={warnings
            .slice(0, 8)
            .map((w) => w.message)
            .join(' · ')}
        />
      ) : null}
    </Space>
  );
};
